import type { FlowTreeNode } from '@vk-hr-tek/ui/FlowTree';

import {
  Workflow,
  WorkflowNode,
  WorkflowManagerBlock,
  WorkflowTransition as Transition,
  WorkflowNodeAction,
} from '@app/gen/settings';

type ActionType =
  | 'upload'
  | 'upload_batch'
  | 'accept'
  | 'decline'
  | 'sign'
  | 'system'
  | 'competency_eval'
  | 'competency_profile'
  | 'competency_fill'
  | 'generate_document_from_template'
  | 'return'
  | 'decline_sign'
  | 'system_booking_hook'
  | 'system_booking_approve'
  | 'system_booking_limits_exceeded_approve'
  | 'system_booking_trip_create'
  | 'system_booking_trip_info'
  | 'system_cancel_request_sw'
  | 'system_booking_trip_ordering'
  | 'system_booking_trip_limit'
  | 'system_booking_trip_limit_approved'
  | 'system_deadline'
  | 'system_create_request_sw'
  | 'system_trip_info_sw'
  | 'system_item_voucher_sw'
  | 'system_cancel_request_sw'
  | 'system_business_trip_file_delivery'
  | 'system_linkedtrip_attrinfo'
  | 'system_linkedtrip_approveinfo'
  | 'system_booking_trip_change';

type Link = { nodeId: string; actionType: ActionType };

export class SettingsWorkflowMapper {
  private links: Map<string, Link[]>;

  private nodes: WorkflowNode[];

  private managerBlocks: WorkflowManagerBlock[];

  private employeeText: string | undefined;

  private setEmployeeText(employeeText?: string) {
    this.employeeText = employeeText;
  }

  private setNodes(nodes: WorkflowNode[]) {
    this.nodes = nodes;
  }

  private setManagerBlocks(managerBlocks: WorkflowManagerBlock[]) {
    this.managerBlocks = managerBlocks;
  }

  private setLinks(transitions: Transition[]) {
    this.links = new Map();
    for (const {
      from_node: from,
      to_node: to,
      action_type: actionType,
    } of transitions) {
      if (actionType !== 'return') {
        const value = this.links.get(from) ?? [];
        value.push({ nodeId: to, actionType });
        this.links.set(from, value);
      }
    }
  }

  private getStartNodeId(transitions: Transition[]): string | null {
    const destinations: Set<string> = new Set(
      transitions.map(({ to_node }) => to_node),
    );

    for (const { from_node: id } of transitions) {
      if (!destinations.has(id)) {
        return id;
      }
    }

    return null;
  }

  private getPrimaryAction(
    actions: WorkflowNodeAction[],
  ): WorkflowNodeAction | null {
    return actions.find(({ is_primary }) => is_primary) ?? null;
  }

  private getNodeTitle(actionType: ActionType): string {
    switch (actionType) {
      case 'upload':
      case 'upload_batch':
      case 'generate_document_from_template':
        return 'Загрузка';
      case 'accept':
        return 'Проверка';
      case 'decline':
        return 'Отказ';
      case 'decline_sign':
        return 'Отказано';
      case 'sign':
        return 'Подписание';
      case 'competency_eval':
        return 'Заполнение';
      case 'competency_profile':
        return 'Заполнение';
      case 'system':
      default:
        return 'Обработка';
    }
  }

  private isConstriction(links: Link[]): boolean {
    return (
      links.length > 1 &&
      links.every(({ actionType }) => actionType === links[0].actionType)
    );
  }

  private getStopConstrictionNodeId(links: Link[]): string | null {
    const ids = new Set<string>();

    for (const { nodeId } of links) {
      let next = this.links.get(nodeId);

      while (next?.length) {
        for (const { nodeId: id } of next) {
          if (ids.has(id)) {
            return id;
          }

          ids.add(id);
          // У нас нет таких флоу, у параллельные процессы могли бы ветвиться или распараллеливаться, соответсвтенно, если next.length > 1, это значит у нас некорректный флоу,
          // если же в будущем появятся такие флоу, алгоритм надо будет усложнять...
          next = this.links.get(id);
        }
      }
    }

    return null;
  }

  private getDescendants(links: Link[], stopNodeId?: string | null) {
    return links.reduce((res: FlowTreeNode[], { nodeId }) => {
      const child = this.getNode(nodeId, { stopNodeId });

      if (child) {
        res.push(child);
      }
      return res;
    }, []);
  }

  private getConstrictionNode(
    nodeId: string,
    links: Link[],
    stopNodeId: string | null,
  ): FlowTreeNode {
    const child = stopNodeId ? this.getNode(stopNodeId, { merge: true }) : null;

    return {
      id: `constriction-to-${nodeId}`,
      state: 'edit',
      enabled: true,
      title: 'constriction',
      constrictOf: this.getDescendants(links, stopNodeId),
      children: child ? [child] : [],
    };
  }

  private getNode(
    nodeId: string,
    {
      first,
      merge = false,
      stopNodeId,
    }: {
      first?: boolean;
      merge?: boolean;
      stopNodeId?: string | null;
    } = {},
  ): FlowTreeNode | null {
    if (stopNodeId && nodeId === stopNodeId) {
      return null;
    }

    const rawNode = this.nodes.find(({ id }) => id === nodeId);

    if (rawNode) {
      const {
        id,
        deadline_settings: deadlineSettings,
        actions,
        name,
        is_optional: isOptional,
      } = rawNode;

      const primaryAction = this.getPrimaryAction(actions);

      const currentManagerBlock =
        this.managerBlocks &&
        this.managerBlocks.find(({ node_ids }) => node_ids[0] === id);

      if (primaryAction) {
        const { type, roles = [], is_employee: isEmployee } = primaryAction;

        const node: FlowTreeNode = {
          id,
          title: '',
          state: 'edit',
          enabled: true,
          executor: {
            position: isEmployee ? this.employeeText : (roles[0] ?? ''),
          },
          info: actions.reduce(
            (res: string[], { document_type_name: documentName }) => {
              if (documentName && !res.includes(documentName)) {
                res.push(documentName);
              }

              return res;
            },
            [],
          ),
          optionalNode: isOptional,
          ...(deadlineSettings?.mode && { mode: deadlineSettings?.mode }),
        };

        node.tooltip = {
          title: this.getNodeTitle(type),
          executor: {
            position: isEmployee ? this.employeeText : (roles[0] ?? ''),
          },
          info: actions.reduce(
            (res: string[], { document_type_name: documentName }) => {
              if (documentName && !res.includes(documentName)) {
                res.push(documentName);
              }

              return res;
            },
            [],
          ),
        };

        if (isOptional) {
          node.tooltip.subtitle = 'Опциональный этап';
        }

        node.deadline = [];
        node.hasDeadline = false;
        if (!!deadlineSettings?.settings?.length) {
          node.deadline = deadlineSettings?.settings.map(
            ({
              reference,
              time_unit: unit,
              outcome,
              term,
              round_days: round,
              attribute,
            }) => ({
              reference,
              unit,
              outcome,
              term,
              round,
              ...(attribute && {
                attribute: attribute?.name ?? attribute?.id ?? '',
              }),
            }),
          );
          node.hasDeadline = true;
        }
        if (currentManagerBlock) {
          const lastNodeIdInManagerBlock =
            currentManagerBlock?.node_ids[
              currentManagerBlock?.node_ids.length - 1
            ];

          const nodeIdAfterManagerBlock = this.links.get(
            lastNodeIdInManagerBlock,
          );

          let newStopId = '';

          if (nodeIdAfterManagerBlock?.length) {
            newStopId = nodeIdAfterManagerBlock[0].nodeId;
          }

          node.managerBlockStart = true;
          node.managerBlockNodeType = type;
          node.managerBlockStatus = 'default';
          node.managerBlockLength = currentManagerBlock.node_ids.length;
          node.managerBlockType = currentManagerBlock.manager_type;
          node.managerBlocksLinks = currentManagerBlock.node_ids;
          node.managerBlockNode = this.getNode(
            currentManagerBlock.node_ids[1],
            {
              stopNodeId: newStopId,
            },
          ) as FlowTreeNode;
        }

        if (this.links.has(id)) {
          const nextNodeLinks = this.links.get(id);

          if (currentManagerBlock) {
            const blockAfterManagerLink = this.links.get(
              currentManagerBlock?.node_ids[
                currentManagerBlock?.node_ids.length - 1
              ],
            );

            if (blockAfterManagerLink) {
              node.children = this.getDescendants(
                blockAfterManagerLink as Link[],
                stopNodeId,
              );
            }
          } else if (nextNodeLinks?.length) {
            if (this.isConstriction(nextNodeLinks)) {
              node.children = [
                this.getConstrictionNode(
                  nodeId,
                  nextNodeLinks,
                  this.getStopConstrictionNodeId(nextNodeLinks),
                ),
              ];
              if (type === 'system') {
                node.constant = true;
              }
            } else {
              node.children = this.getDescendants(nextNodeLinks, stopNodeId);
            }
          }
        }

        if (first) {
          node.title = 'Старт';
          node.tooltip.title = 'Старт';
          node.constant = true;
        } else if (
          !node.children?.length &&
          !stopNodeId &&
          !node.managerBlockStart
        ) {
          node.title = name || 'Финиш';
          node.tooltip.title = name || 'Финиш';
          node.constant = true;
        } else {
          node.title = this.getNodeTitle(type);
          if (merge && type === 'system') {
            node.constant = true;
          }
        }

        return node;
      }
    }

    return null;
  }

  public transform(
    workflow?: Workflow,
    employeeText?: string,
  ): FlowTreeNode | null {
    if (workflow) {
      const { nodes, transitions, manager_blocks: managerBlocks } = workflow;

      this.setNodes(nodes);
      this.setLinks(transitions);
      this.setManagerBlocks(managerBlocks);
      this.setEmployeeText(employeeText);

      const startNodeId = this.getStartNodeId(transitions);

      if (startNodeId) {
        return this.getNode(startNodeId, { first: true });
      }
    }

    return null;
  }
}
